/**
 * Notiz aus dem Gespräch — Entwurf und Speichern, ohne React.
 *
 * Während des Telefonats gibt es noch keine `CallLog`-Zeile, nur die
 * SDK-Gesprächs-ID. Der Text lebt deshalb als Entwurf je callId: im
 * TelephonyContext und zusätzlich in `sessionStorage`
 * (`kmvs.anrufNotiz.<callId>`), damit ein Neuladen des Tabs ihn nicht
 * verschluckt. Nach dem Auflegen wird er an die Zeile ANGEHÄNGT
 * (`PATCH telephony/calls/:id { appendNotes }`) — nie überschrieben.
 *
 * Grundsatz: Eine Notiz geht nie still verloren. Solange sie nicht sicher am
 * Anruf steht, bleibt der Entwurf im Speicher und der Aufrufer sagt es laut.
 */

export const ANRUF_NOTIZ_PREFIX = "kmvs.anrufNotiz.";

/** Das, was wir von `Storage` brauchen — in Tests durch eine Map ersetzbar. */
export interface NotizSpeicher {
    readonly length: number;
    key(index: number): string | null;
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

/** `sessionStorage`, falls erreichbar (privater Modus/blockiert → null). */
export function sitzungsSpeicher(): NotizSpeicher | null {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

export function entwurfSchluessel(callId: string): string {
    return `${ANRUF_NOTIZ_PREFIX}${callId}`;
}

export function entwurfLesen(callId: string, speicher: NotizSpeicher | null = sitzungsSpeicher()): string {
    if (!speicher) return "";
    try {
        return speicher.getItem(entwurfSchluessel(callId)) ?? "";
    } catch {
        return "";
    }
}

/** Leerer (oder nur Leerraum-)Text löscht den Entwurf. */
export function entwurfSchreiben(
    callId: string,
    text: string,
    speicher: NotizSpeicher | null = sitzungsSpeicher(),
): void {
    if (!speicher) return;
    try {
        if (text.trim()) speicher.setItem(entwurfSchluessel(callId), text);
        else speicher.removeItem(entwurfSchluessel(callId));
    } catch {
        /* Speicher voll/blockiert — der Entwurf lebt dann nur im Context */
    }
}

export function entwurfLoeschen(callId: string, speicher: NotizSpeicher | null = sitzungsSpeicher()): void {
    if (!speicher) return;
    try {
        speicher.removeItem(entwurfSchluessel(callId));
    } catch {
        /* ignore */
    }
}

/** Alle liegengebliebenen Entwürfe (z. B. nach einem Neuladen mitten im Gespräch). */
export function offeneEntwuerfe(
    speicher: NotizSpeicher | null = sitzungsSpeicher(),
): Array<{ callId: string; text: string }> {
    if (!speicher) return [];
    const out: Array<{ callId: string; text: string }> = [];
    try {
        for (let i = 0; i < speicher.length; i++) {
            const k = speicher.key(i);
            if (!k || !k.startsWith(ANRUF_NOTIZ_PREFIX)) continue;
            const text = speicher.getItem(k) ?? "";
            const callId = k.slice(ANRUF_NOTIZ_PREFIX.length);
            if (callId && text.trim()) out.push({ callId, text });
        }
    } catch {
        return out;
    }
    return out;
}

export type NotizSpeicherErgebnis = "gespeichert" | "leer" | "nichtGefunden" | "fehler";

export interface NotizSpeichernDeps {
    /** Antwort des Ingest (`softphone.callLogIdFor`) — `null`, wenn es keinen gab. */
    callLogId: Promise<number | null> | null;
    /** Die Zeile per Gesprächs-ID suchen (`GET telephony/calls?callId=`). */
    suchen: (callId: string) => Promise<number | null>;
    /** Text anhängen (`PATCH telephony/calls/:id { appendNotes }`). */
    anhaengen: (callLogId: number, text: string) => Promise<void>;
}

/**
 * Den Entwurf an seinen Anruf hängen: erst die ID aus dem Ingest, sonst EINMAL
 * per Gesprächs-ID suchen. Wirft nie — das Ergebnis sagt, was passiert ist,
 * und der Aufrufer entscheidet, ob der Entwurf weg darf (nur bei „gespeichert").
 */
export async function notizAnAnrufSpeichern(
    callId: string,
    text: string,
    deps: NotizSpeichernDeps,
): Promise<NotizSpeicherErgebnis> {
    const inhalt = text.trim();
    if (!inhalt) return "leer";
    let id: number | null = null;
    try {
        id = deps.callLogId ? await deps.callLogId : null;
    } catch {
        id = null;
    }
    if (id == null) {
        try {
            id = await deps.suchen(callId);
        } catch {
            id = null;
        }
    }
    if (id == null) return "nichtGefunden";
    try {
        await deps.anhaengen(id, inhalt);
        return "gespeichert";
    } catch {
        return "fehler";
    }
}

/**
 * Fenster-Ereignis, sobald eine Notiz aus dem Gespräch am Anruf steht
 * (08.10.2026: „Notiz wird nicht angezeigt"). Die Telefonie-Seite hatte die
 * Zeile meist schon geladen, bevor die Notiz angehängt war, und zeigte sie
 * erst nach einem Neuladen.
 */
export const ANRUF_NOTIZ_GESPEICHERT = "kmvs:anruf-notiz-gespeichert";

export interface AnrufNotizGespeichert {
    callLogId: number;
    notes: string | null;
}

export function meldeNotizGespeichert(detail: AnrufNotizGespeichert): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent<AnrufNotizGespeichert>(ANRUF_NOTIZ_GESPEICHERT, { detail }));
}

/** Kurzfassung einer Notiz für einen Hinweis („…" nach 80 Zeichen). */
export function notizKurz(text: string, max = 80): string {
    const t = text.trim().replace(/\s+/g, " ");
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/**
 * Darf das Notizfeld im Anruf-Popup den Fokus nehmen, wenn es erscheint (T-13)?
 * Nein, wenn der Nutzer gerade außerhalb des Widgets in einem Eingabefeld tippt
 * (Anruf kommt rein, Annahme am Headset, ausgehender Anruf verbindet) — sonst
 * landen seine Tasten in der Notiz.
 */
export function darfNotizFokusNehmen(
    el: { tagName: string; isContentEditable?: boolean } | null,
    innerhalbWidget: boolean,
): boolean {
    if (!el || innerhalbWidget) return true;
    if (el.isContentEditable) return false;
    const tag = el.tagName.toUpperCase();
    return tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT";
}
